import { adjust, darken, invert, isDark, lighten, rgba } from 'khroma';
import { applyOverride, mkBorder } from './theme-helpers.js';
import {
  oldAttributeBackgroundColorEven,
  oldAttributeBackgroundColorOdd,
} from './erDiagram-oldHardcodedValues.js';

class Theme {
  constructor() {
    /** # Base variables */
    /**
     * - Background - used to know what the background color is of the diagram. This is used for
     *   deducing colors for instance line color. Default value is #f4f4f4.
     */
    this.background = '#333';
    this.primaryColor = '#1f2020';
    this.secondaryColor = lighten(this.primaryColor, 16);
    this.tertiaryColor = adjust(this.primaryColor, { h: -160 });
    this.primaryBorderColor = invert(this.background);
    this.secondaryBorderColor = mkBorder(this.secondaryColor, this.darkMode);
    this.tertiaryBorderColor = mkBorder(this.tertiaryColor, this.darkMode);
    this.primaryTextColor = invert(this.primaryColor);
    this.secondaryTextColor = invert(this.secondaryColor);
    this.tertiaryTextColor = invert(this.tertiaryColor);

    this.mainBkg = '#111113';
    this.secondBkg = 'calculated'; // resolved in updateColors()
    this.mainContrastColor = 'lightgrey';
    this.darkTextColor = lighten(invert('#323D47'), 10);
    this.border1 = '#ccc';
    this.border2 = rgba(255, 255, 255, 0.25);
    this.arrowheadColor = invert(this.background);
    this.fontFamily = '"Recursive Variable", arial, sans-serif';
    this.fontSize = '14px';
    this.labelBackground = '#111113';
    this.textColor = '#ccc';
    this.THEME_COLOR_LIMIT = 12;
    this.radius = 12;
    this.strokeWidth = 2;

    this.noteBkgColor = this.noteBkgColor ?? '#FEF9C3';
    this.noteTextColor = this.noteTextColor ?? '#28253D';

    this.THEME_COLOR_LIMIT = 12;
    // dark
    this.fontFamily = '"Recursive Variable", arial, sans-serif';
    this.fontSize = '14px';

    // Neo-specific
    this.nodeBorder = '#FFFFFF';
    this.stateBorder = '#FFFFFF';

    this.useGradient = false;
    this.gradientStart = '#0042eb';
    this.gradientStop = '#eb0042';
    this.dropShadow = 'url(#drop-shadow)';
    this.nodeShadow = true;

    /* Architecture Diagram variables */
    this.archEdgeColor = 'calculated';
    this.archEdgeArrowColor = 'calculated';
    this.archEdgeWidth = '3';
    this.archGroupBorderColor = this.primaryBorderColor;
    this.archGroupBorderWidth = '2px';

    /* Class Diagram variables */
    this.clusterBkg = '#1E1A2E';
    this.clusterBorder = '#BDBCCC';
    this.noteBorderColor = '#FACC15';

    /* Sequence Diagram variables */
    this.noteFontWeight = 600;

    this.borderColorArray = [
      '#E879F9', //Fuchsia-400
      '#2DD4BF', //Teal-400
      '#FB923C', //Orange-400
      '#22D3EE', // Cyan-400
      '#4ADE80', // Green-400
      '#A78BFA', //Violet-400
      '#F87171', //red-400
      '#FACC15', //yellow-400
      '#818CF8', //indigo-400
      '#A3E635 ', //Lime-400
      '#38BDF8', //Sky-400
      '#FB7185', //Rose-400
    ];

    this.bkgColorArray = [];

    /* Usecase Diagram variables -- the dark counterpart of the light theme's role tokens.

       Borders only. This theme already ships a border palette and an *empty* background
       palette, because saturated fills behind light text lose contrast fast in dark mode --
       so the role tokens follow the same rule and set no fills at all. Leaving them unset
       lets each one fall back to the value the rest of the theme already uses: `mainBkg`
       for actors and use cases, `clusterBkg` for boundaries. Pinning them to one colour
       instead flattened the three kinds into a single tone and made a use case body
       indistinguishable from the boundary containing it. */
    this.usecaseActorBorder = '#A78BFA'; // Violet-400
    this.usecaseBorder = '#2DD4BF'; // Teal-400
    this.usecaseBoundaryBorder = '#BDBCCC';
    this.usecaseIncludeLine = '#38BDF8'; // Sky-400
    this.usecaseExtendLine = '#FB923C'; // Orange-400

    this.filterColor = '#FFFFFF';
  }
  updateColors() {
    // The || is to make sure that if the variable has been defined by a user override that value is to be used

    /* Main */
    this.primaryTextColor = this.primaryTextColor || (this.darkMode ? '#eee' : '#FFFFFF'); // invert(this.primaryColor);
    this.secondaryColor = this.secondaryColor || adjust(this.primaryColor, { h: -120 });
    this.tertiaryColor = this.tertiaryColor || adjust(this.primaryColor, { h: 180, l: 5 });

    this.primaryBorderColor = this.primaryBorderColor || mkBorder(this.primaryColor, this.darkMode);
    this.secondaryBorderColor =
      this.secondaryBorderColor || mkBorder(this.secondaryColor, this.darkMode);
    this.tertiaryBorderColor =
      this.tertiaryBorderColor || mkBorder(this.tertiaryColor, this.darkMode);
    this.noteBorderColor = this.noteBorderColor || mkBorder(this.noteBkgColor, this.darkMode);
    this.noteBkgColor = this.noteBkgColor || '#fff5ad';
    this.noteTextColor = this.noteTextColor || '#FFFFFF';

    this.secondaryTextColor = this.secondaryTextColor || invert(this.secondaryColor);
    this.tertiaryTextColor = this.tertiaryTextColor || invert(this.tertiaryColor);
    this.lineColor = this.lineColor || invert(this.background);
    this.arrowheadColor = this.arrowheadColor || invert(this.background);
    this.textColor = this.textColor || this.primaryTextColor;

    // TODO: should this instead default to secondaryBorderColor?
    this.border2 = this.border2 || this.tertiaryBorderColor;

    /* Flowchart variables */
    this.nodeBkg = this.nodeBkg || this.primaryColor;
    this.mainBkg = this.mainBkg || this.primaryColor;
    /* Resolve the ctor's 'calculated' placeholder, which this theme inherited from
     * theme-dark.js without the line that computes it -- so it shipped as the literal
     * string, and every consumer (railroad's terminal fill) emitted `fill: calculated`. */
    this.secondBkg = this.secondBkg === 'calculated' ? lighten(this.mainBkg, 16) : this.secondBkg;
    this.nodeBorder = this.nodeBorder || this.border1;
    this.clusterBkg = this.clusterBkg || this.tertiaryColor;
    this.clusterBorder = this.clusterBorder || this.tertiaryBorderColor;
    this.defaultLinkColor = this.defaultLinkColor || this.lineColor;
    this.titleColor = this.titleColor || this.tertiaryTextColor;
    this.edgeLabelBackground =
      this.edgeLabelBackground ||
      (this.darkMode ? darken(this.secondaryColor, 30) : this.secondaryColor);
    this.nodeTextColor = this.nodeTextColor || this.primaryTextColor;
    /* Agentflow variables */
    this.flowContainerStroke = this.flowContainerStroke || this.secondaryBorderColor;

    /* Sequence Diagram variables */

    this.actorBorder = '#FFFFFF';
    this.signalColor = '#FFFFFF';
    this.labelBoxBorderColor = '#BDBCCC';
    this.actorBorder = this.actorBorder || this.primaryBorderColor;
    this.actorBkg = this.actorBkg || this.mainBkg;
    this.actorTextColor = this.actorTextColor || this.primaryTextColor;
    this.actorLineColor = this.actorLineColor || this.actorBorder;
    this.labelBoxBkgColor = this.labelBoxBkgColor || this.actorBkg;
    this.signalColor = this.signalColor || this.textColor;
    this.signalTextColor = this.signalTextColor || this.textColor;
    this.labelBoxBorderColor = this.labelBoxBorderColor || this.actorBorder;
    this.labelTextColor = this.labelTextColor || this.actorTextColor;
    this.loopTextColor = this.loopTextColor || this.actorTextColor;
    this.activationBorderColor = this.activationBorderColor || darken(this.secondaryColor, 10);
    this.activationBkgColor = this.activationBkgColor || this.secondaryColor;
    this.sequenceNumberColor = this.sequenceNumberColor || invert(this.lineColor);
    this.rectBkgColor = this.rectBkgColor || this.tertiaryColor;

    /* Mindmap Diagram variables */
    this.rootLabelColor = '#FFFFFF';

    /* Gantt chart variables */

    this.excludeBkgColor = this.excludeBkgColor || '#eeeeee';
    this.taskBorderColor = this.taskBorderColor || this.primaryBorderColor;
    this.taskBkgColor = this.taskBkgColor || this.primaryColor;
    this.activeTaskBorderColor = this.activeTaskBorderColor || this.primaryColor;
    this.activeTaskBkgColor = this.activeTaskBkgColor || lighten(this.primaryColor, 23);
    this.gridColor = this.gridColor || 'lightgrey';
    /* `doneTaskBkgColor` was 'lightgrey' -- a light fill under this theme's light
     * `taskTextDarkColor`, giving 1.07:1 on done-task labels. `theme-dark.js` gets away
     * with the same fill because it pairs it with a dark ink; here the active-task fill is
     * dark, so one ink has to serve both and the fill is what has to move. */
    this.doneTaskBkgColor = this.doneTaskBkgColor || this.secondBkg;
    this.doneTaskBorderColor = this.doneTaskBorderColor || 'grey';
    this.critBorderColor = this.critBorderColor || '#ff8888';
    this.critBkgColor = this.critBkgColor || 'red';
    this.todayLineColor = this.todayLineColor || 'red';
    this.taskTextColor = this.taskTextColor || this.textColor;
    this.vertLineColor = this.vertLineColor || this.primaryBorderColor;
    this.taskTextOutsideColor = this.taskTextOutsideColor || this.textColor;
    this.taskTextLightColor = this.taskTextLightColor || this.textColor;
    this.taskTextColor = this.taskTextColor || this.primaryTextColor;
    this.taskTextDarkColor = this.taskTextDarkColor || this.textColor;
    this.taskTextClickableColor = this.taskTextClickableColor || '#003163';

    /* Architecture Diagram variables */
    this.archEdgeColor = this.lineColor;
    this.archEdgeArrowColor = this.lineColor;

    /* Sequence Diagram variables */

    this.personBorder = this.personBorder || this.primaryBorderColor;
    this.personBkg = this.personBkg || this.mainBkg;

    /* state colors */
    this.transitionColor = this.transitionColor || this.lineColor;
    this.transitionLabelColor = this.transitionLabelColor || this.textColor;
    /* The color of the text tables of the states*/
    this.stateLabelColor = this.stateLabelColor || this.stateBkg || this.primaryTextColor;
    this.stateEdgeLabelBackground = this.stateEdgeLabelBackground || '#16141F';
    this.stateBkg = this.stateBkg || this.mainBkg;
    this.labelBackgroundColor = this.labelBackgroundColor || this.stateBkg;
    this.compositeBackground = this.compositeBackground || '#16141F';
    this.altBackground = this.altBackground || '#16141F';
    this.compositeTitleBackground = this.compositeTitleBackground || '#16141F';
    this.compositeBorder = this.compositeBorder || this.nodeBorder;
    this.innerEndBackground = this.nodeBorder;
    this.errorBkgColor = this.errorBkgColor || this.tertiaryColor;
    this.errorTextColor = this.errorTextColor || this.tertiaryTextColor;
    this.transitionColor = this.transitionColor || this.lineColor;
    this.specialStateColor = this.lineColor;

    /* Color Scale */
    /* Each color-set will have a background, a foreground and a border color */
    this.cScale0 = this.cScale0 || '#f4a8ff'; // Fuchsia-300
    this.cScale1 = this.cScale1 || '#46ecd5'; // Teal-300
    this.cScale2 = this.cScale2 || '#ffb86a'; // Orange-300
    this.cScale3 = this.cScale3 || '#dab2ff'; // Purple-300
    this.cScale4 = this.cScale4 || '#7bf1a8'; // Green-300
    this.cScale5 = this.cScale5 || '#c4b4ff'; // Violet-300
    this.cScale6 = this.cScale6 || '#ffa2a2'; // Red-300
    this.cScale7 = this.cScale7 || '#ffdf20'; // Yellow-300
    this.cScale8 = this.cScale8 || '#a3b3ff'; // Indigo-300
    this.cScale9 = this.cScale9 || '#bbf451'; // Lime-300
    this.cScale10 = this.cScale10 || '#74d4ff'; // Sky-300
    this.cScale11 = this.cScale11 || '#ffa1ad'; // Rose-300

    // if (this.darkMode) {
    //   for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
    //     this['cScale' + i] = darken(this['cScale' + i], 75);
    //   }
    // } else {
    //   for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
    //     this['cScale' + i] = darken(this['cScale' + i], 25);
    //   }
    // }

    /* Gantt chart section banding.
     *
     * gantt/styles.js cycles four band classes: .section0 -> sectionBkgColor,
     * .section1 and .section3 -> altSectionBkgColor, .section2 -> sectionBkgColor2. All
     * are painted at 20% opacity, so the source colour has to be saturated to read at
     * all -- the `primaryColor` tints used before composited to nothing.
     *
     * Assigned here rather than in the gantt block above so the two visible bands come
     * off the categorical scale and follow it if the palette changes.
     *
     * altSectionBkgColor is the canvas colour, not the inherited 'white': at 20% over
     * the #333 canvas white composites to rgb(92,92,92), a grey brighter than both tuned
     * hues, so half of every gantt's banding fought the other half. The canvas colour
     * makes those bands read as absent instead, matching the light theme.
     */
    this.sectionBkgColor = this.sectionBkgColor || this.cScale0;
    this.sectionBkgColor2 = this.sectionBkgColor2 || this.cScale1;
    this.altSectionBkgColor = this.altSectionBkgColor || this.background;

    // Setup the inverted color for the set
    for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
      this['cScaleInv' + i] = this['cScaleInv' + i] || invert(this['cScale' + i]);
    }
    // Setup the peer color for the set, useful for borders
    for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
      if (this.darkMode) {
        this['cScalePeer' + i] = this['cScalePeer' + i] || lighten(this['cScale' + i], 10);
      } else {
        this['cScalePeer' + i] = this['cScalePeer' + i] || darken(this['cScale' + i], 10);
      }
    }

    // Setup the label color for the set
    this.scaleLabelColor = this.scaleLabelColor || this.labelTextColor;

    for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
      this['cScaleLabel' + i] = darken(this['cScale' + i], 75);
    }

    const multiplier = this.darkMode ? -4 : -1;
    for (let i = 0; i < 5; i++) {
      this['surface' + i] =
        this['surface' + i] ||
        adjust(this.mainBkg, { h: 180, s: -15, l: multiplier * (5 + i * 3) });
      this['surfacePeer' + i] =
        this['surfacePeer' + i] ||
        adjust(this.mainBkg, { h: 180, s: -15, l: multiplier * (8 + i * 3) });
    }

    /* class */
    this.classText = this.classText || this.textColor;

    /* user-journey */
    // Journey paints the section band and the task rects inside it with the same
    // `fillTypeN`, and task labels use the light `textColor`, so these fills have to
    // stay dark -- a saturated fill collapses band and task into one block and swamps
    // the labels. `bkgColorArray` is empty in this theme, so there is no pale array to
    // draw on, and darkening the categorical scale by a fixed amount does not work
    // either: the 300-shades differ too much in luminance, so teal lands near black
    // while fuchsia stays clearly purple. Fixed 900-shades of the same hue order give
    // even weight across the eight sections.
    const journeyFills = [
      '#701a75', // Fuchsia-900
      '#134e4a', // Teal-900
      '#7c2d12', // Orange-900
      '#581c87', // Purple-900
      '#14532d', // Green-900
      '#4c1d95', // Violet-900
      '#7f1d1d', // Red-900
      '#713f12', // Yellow-900
    ];
    journeyFills.forEach((fill, i) => {
      this['fillType' + i] = this['fillType' + i] || fill;
    });

    /* pie */
    // Slices reuse the theme's categorical scale so a pie reads like a mindmap or
    // treemap in the same theme. The old tints of `primaryColor` were all near-black
    // on a dark canvas, leaving adjacent slices indistinguishable.
    for (let i = 0; i < this.THEME_COLOR_LIMIT; i++) {
      this['pie' + (i + 1)] = this['pie' + (i + 1)] || this['cScale' + i];
    }
    this.pieTitleTextSize = this.pieTitleTextSize || '25px';
    this.pieTitleTextColor = this.pieTitleTextColor || this.taskTextDarkColor;
    this.pieSectionTextSize = this.pieSectionTextSize || '17px';
    // Slice fills are now the bright categorical scale, so the in-slice label needs a
    // dark ink rather than the theme's light `textColor`.
    this.pieSectionTextColor = this.pieSectionTextColor || '#28253D';
    this.pieLegendTextSize = this.pieLegendTextSize || '17px';
    this.pieLegendTextColor = this.pieLegendTextColor || this.taskTextDarkColor;
    this.pieStrokeColor = this.pieStrokeColor || 'black';
    this.pieStrokeWidth = this.pieStrokeWidth || '2px';
    this.pieOuterStrokeWidth = this.pieOuterStrokeWidth || '2px';
    this.pieOuterStrokeColor = this.pieOuterStrokeColor || 'black';
    this.pieOpacity = this.pieOpacity || '0.7';

    /* venn */
    /* `borderColorArray` and not the lighter `cScale`: the fill is painted at 0.1
       opacity, so the stroke and the label carry the circle. */
    for (let i = 0; i < 8; i++) {
      this['venn' + (i + 1)] =
        this['venn' + (i + 1)] ?? this.borderColorArray[i % this.borderColorArray.length];
    }
    this.vennTitleTextColor = this.vennTitleTextColor ?? this.titleColor;
    this.vennSetTextColor = this.vennSetTextColor ?? this.textColor;

    /* quadrant-graph */
    this.quadrant1Fill = this.quadrant1Fill || this.primaryColor;
    this.quadrant2Fill = this.quadrant2Fill || adjust(this.primaryColor, { r: 5, g: 5, b: 5 });
    this.quadrant3Fill = this.quadrant3Fill || adjust(this.primaryColor, { r: 10, g: 10, b: 10 });
    this.quadrant4Fill = this.quadrant4Fill || adjust(this.primaryColor, { r: 15, g: 15, b: 15 });
    this.quadrant1TextFill = this.quadrant1TextFill || this.primaryTextColor;
    this.quadrant2TextFill =
      this.quadrant2TextFill || adjust(this.primaryTextColor, { r: -5, g: -5, b: -5 });
    this.quadrant3TextFill =
      this.quadrant3TextFill || adjust(this.primaryTextColor, { r: -10, g: -10, b: -10 });
    this.quadrant4TextFill =
      this.quadrant4TextFill || adjust(this.primaryTextColor, { r: -15, g: -15, b: -15 });
    this.quadrantPointFill =
      this.quadrantPointFill || isDark(this.quadrant1Fill)
        ? lighten(this.quadrant1Fill)
        : darken(this.quadrant1Fill);
    this.quadrantPointTextFill = this.quadrantPointTextFill || this.primaryTextColor;
    this.quadrantXAxisTextFill = this.quadrantXAxisTextFill || this.primaryTextColor;
    this.quadrantYAxisTextFill = this.quadrantYAxisTextFill || this.primaryTextColor;
    this.quadrantInternalBorderStrokeFill =
      this.quadrantInternalBorderStrokeFill || this.primaryBorderColor;
    this.quadrantExternalBorderStrokeFill =
      this.quadrantExternalBorderStrokeFill || this.primaryBorderColor;
    this.quadrantTitleFill = this.quadrantTitleFill || this.primaryTextColor;

    /* xychart */
    this.xyChart = {
      backgroundColor: this.xyChart?.backgroundColor || this.background,
      titleColor: this.xyChart?.titleColor || this.primaryTextColor,
      legendTextColor: this.xyChart?.legendTextColor || this.primaryTextColor,
      xAxisTitleColor: this.xyChart?.xAxisTitleColor || this.primaryTextColor,
      xAxisLabelColor: this.xyChart?.xAxisLabelColor || this.primaryTextColor,
      xAxisTickColor: this.xyChart?.xAxisTickColor || this.primaryTextColor,
      xAxisLineColor: this.xyChart?.xAxisLineColor || this.primaryTextColor,
      yAxisTitleColor: this.xyChart?.yAxisTitleColor || this.primaryTextColor,
      yAxisLabelColor: this.xyChart?.yAxisLabelColor || this.primaryTextColor,
      yAxisTickColor: this.xyChart?.yAxisTickColor || this.primaryTextColor,
      yAxisLineColor: this.xyChart?.yAxisLineColor || this.primaryTextColor,
      plotColorPalette:
        this.xyChart?.plotColorPalette ||
        '#FFF4DD,#FFD8B1,#FFA07A,#ECEFF1,#D6DBDF,#C3E0A8,#FFB6A4,#FFD74D,#738FA7,#FFFFF0',
    };

    /* requirement-diagram */
    this.requirementBackground = this.requirementBackground || this.primaryColor;
    this.requirementBorderColor = this.requirementBorderColor || this.primaryBorderColor;
    this.requirementBorderSize = this.requirementBorderSize || '1';
    this.requirementTextColor = this.requirementTextColor || this.primaryTextColor;
    this.relationColor = this.relationColor || this.lineColor;
    this.relationLabelBackground =
      this.relationLabelBackground ||
      (this.darkMode ? darken(this.secondaryColor, 30) : this.secondaryColor);
    this.relationLabelColor = this.relationLabelColor || this.actorTextColor;
    this.requirementEdgeLabelBackground = '#16141F';

    /* git */
    this.git0 = this.git0 || this.primaryColor;
    this.git1 = this.git1 || this.secondaryColor;
    this.git2 = this.git2 || this.tertiaryColor;
    this.git3 = this.git3 || adjust(this.primaryColor, { h: -30 });
    this.git4 = this.git4 || adjust(this.primaryColor, { h: -60 });
    this.git5 = this.git5 || adjust(this.primaryColor, { h: -90 });
    this.git6 = this.git6 || adjust(this.primaryColor, { h: +60 });
    this.git7 = this.git7 || adjust(this.primaryColor, { h: +120 });
    if (this.darkMode) {
      this.git0 = lighten(this.git0, 25);
      this.git1 = lighten(this.git1, 25);
      this.git2 = lighten(this.git2, 25);
      this.git3 = lighten(this.git3, 25);
      this.git4 = lighten(this.git4, 25);
      this.git5 = lighten(this.git5, 25);
      this.git6 = lighten(this.git6, 25);
      this.git7 = lighten(this.git7, 25);
    } else {
      this.git0 = darken(this.git0, 25);
      this.git1 = darken(this.git1, 25);
      this.git2 = darken(this.git2, 25);
      this.git3 = darken(this.git3, 25);
      this.git4 = darken(this.git4, 25);
      this.git5 = darken(this.git5, 25);
      this.git6 = darken(this.git6, 25);
      this.git7 = darken(this.git7, 25);
    }
    this.gitInv0 = this.gitInv0 || invert(this.git0);
    this.gitInv1 = this.gitInv1 || invert(this.git1);
    this.gitInv2 = this.gitInv2 || invert(this.git2);
    this.gitInv3 = this.gitInv3 || invert(this.git3);
    this.gitInv4 = this.gitInv4 || invert(this.git4);
    this.gitInv5 = this.gitInv5 || invert(this.git5);
    this.gitInv6 = this.gitInv6 || invert(this.git6);
    this.gitInv7 = this.gitInv7 || invert(this.git7);
    this.branchLabelColor =
      this.branchLabelColor || (this.darkMode ? 'black' : this.labelTextColor);
    this.gitBranchLabel0 = this.gitBranchLabel0 || this.branchLabelColor;
    this.gitBranchLabel1 = this.gitBranchLabel1 || this.branchLabelColor;
    this.gitBranchLabel2 = this.gitBranchLabel2 || this.branchLabelColor;
    this.gitBranchLabel3 = this.gitBranchLabel3 || this.branchLabelColor;
    this.gitBranchLabel4 = this.gitBranchLabel4 || this.branchLabelColor;
    this.gitBranchLabel5 = this.gitBranchLabel5 || this.branchLabelColor;
    this.gitBranchLabel6 = this.gitBranchLabel6 || this.branchLabelColor;
    this.gitBranchLabel7 = this.gitBranchLabel7 || this.branchLabelColor;

    this.tagLabelColor = this.tagLabelColor || this.primaryTextColor;
    this.tagLabelBackground = this.tagLabelBackground || this.primaryColor;
    this.tagLabelBorder = this.tagBorder || this.primaryBorderColor;
    this.tagLabelFontSize = this.tagLabelFontSize || '10px';
    this.commitLabelColor = this.commitLabelColor || this.secondaryTextColor;
    this.commitLabelBackground = this.commitLabelBackground || this.secondaryColor;
    this.commitLabelFontSize = this.commitLabelFontSize || '10px';
    this.commitLineColor = this.commitLineColor ?? '#BDBCCC';
    this.fontWeight = 600;

    /* -------------------------------------------------- */
    /* EntityRelationship diagrams                        */
    this.erEdgeLabelBackground = '#16141F';

    this.attributeBackgroundColorOdd =
      this.attributeBackgroundColorOdd || oldAttributeBackgroundColorOdd;
    this.attributeBackgroundColorEven =
      this.attributeBackgroundColorEven || oldAttributeBackgroundColorEven;
    /* -------------------------------------------------- */
  }
  calculate(overrides) {
    if (typeof overrides !== 'object') {
      // Calculate colors form base colors
      this.updateColors();
      return;
    }

    const keys = Object.keys(overrides);

    // Copy values from overrides, this is mainly for base colors
    keys.forEach((k) => {
      this[k] = overrides[k];
    });

    // Calculate colors form base colors
    this.updateColors();
    // Copy values from overrides again in case of an override of derived value
    keys.forEach((k) => {
      this[k] = applyOverride(this[k], overrides[k]);
    });
  }
}

export const getThemeVariables = (userOverrides) => {
  const theme = new Theme();
  theme.calculate(userOverrides);
  return theme;
};
